import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article' | 'service' | 'organization';
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  schemaType?: 'Organization' | 'WebSite' | 'WebPage' | 'Article' | 'Service';
}

export default function SEO({
  title,
  description,
  canonical = 'https://tecbyte.io',
  image = 'https://tecbyte.io/tecbyte-logo.svg',
  type = 'website',
  keywords = 'AI integration, LLM, large language models, automation, Bangladesh, government AI, secure AI, data intelligence, software solutions, business intelligence',
  author,
  publishedTime,
  modifiedTime,
  schemaType = 'Organization',
}: SEOProps) {
  // Generate appropriate schema based on content type
  const generateSchema = () => {
    const baseSchema = {
      '@context': 'https://schema.org',
    };

    switch (schemaType) {
      case 'Organization':
        return {
          ...baseSchema,
          '@type': 'Organization',
          name: 'Tecbyte',
          alternateName: 'TecByte.io',
          url: 'https://tecbyte.io',
          logo: {
            '@type': 'ImageObject',
            url: 'https://tecbyte.io/tecbyte-logo.svg',
            width: 200,
            height: 60
          },
          description: 'AI Integration for Bangladesh\'s Future - Deploy advanced AI systems that respect your security requirements and regulatory compliance.',
          foundingDate: '2024',
          knowsAbout: ['Artificial Intelligence', 'Large Language Models', 'Government AI Solutions', 'Secure AI Deployment', 'Data Intelligence', 'Process Automation'],
          areaServed: {
            '@type': 'Country',
            name: 'Bangladesh'
          },
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'hello@tecbyte.io',
            contactType: 'customer service',
            availableLanguage: ['English', 'Bengali']
          },
          sameAs: [
            'https://tecbyte.io'
          ],
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'AI Integration Services',
            itemListElement: [
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'AI System Integration',
                  description: 'Secure AI deployment for government and enterprise'
                }
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Large Language Model Implementation',
                  description: 'Custom LLM solutions for organizational needs'
                }
              }
            ]
          }
        };
      
      case 'WebSite':
        return {
          ...baseSchema,
          '@type': 'WebSite',
          name: title,
          description: description,
          url: canonical,
          publisher: {
            '@type': 'Organization',
            name: 'Tecbyte',
            url: 'https://tecbyte.io'
          },
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: 'https://tecbyte.io/search?q={search_term_string}'
            },
            'query-input': 'required name=search_term_string'
          }
        };
      
      case 'WebPage':
        return {
          ...baseSchema,
          '@type': 'WebPage',
          name: title,
          description: description,
          url: canonical,
          isPartOf: {
            '@type': 'WebSite',
            name: 'Tecbyte',
            url: 'https://tecbyte.io'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Tecbyte'
          },
          ...(publishedTime && { datePublished: publishedTime }),
          ...(modifiedTime && { dateModified: modifiedTime }),
          ...(author && { author: { '@type': 'Person', name: author } })
        };
      
      case 'Article':
        return {
          ...baseSchema,
          '@type': 'Article',
          headline: title,
          description: description,
          url: canonical,
          image: image,
          datePublished: publishedTime || new Date().toISOString(),
          dateModified: modifiedTime || new Date().toISOString(),
          author: {
            '@type': 'Organization',
            name: author || 'Tecbyte'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Tecbyte',
            logo: {
              '@type': 'ImageObject',
              url: 'https://tecbyte.io/tecbyte-logo.svg'
            }
          }
        };
      
      case 'Service':
        return {
          ...baseSchema,
          '@type': 'Service',
          name: title,
          description: description,
          provider: {
            '@type': 'Organization',
            name: 'Tecbyte',
            url: 'https://tecbyte.io'
          },
          areaServed: {
            '@type': 'Country',
            name: 'Bangladesh'
          },
          serviceType: 'AI Integration Services',
          audience: {
            '@type': 'Audience',
            audienceType: 'Government Organizations, Enterprises'
          }
        };
      
      default:
        return {
          ...baseSchema,
          '@type': 'Organization',
          name: 'Tecbyte',
          description: description
        };
    }
  };

  const schemaData = generateSchema();

  return (
    <Helmet>
      {/* Basic SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="TecByte.io" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional meta tags */}
      {author && <meta name="author" content={author} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData, null, 2)}
      </script>
    </Helmet>
  );
} 